import React, { useEffect, useState } from 'react';
import {
  createMonitorProfile,
  getCategories,
  getMonitorProfiles,
  getPrintGroups,
  getSettings,
  updateMonitorProfile,
} from '../../../lib/api';
import { canonicalPrintGroupLabel, includesPrintGroup } from '../../../lib/printGroups';
import { normalizeSalesCatalog, SALES_MENU_SCOPE_LABELS } from '../../../lib/salesCatalog';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { CheckCircle2, ExternalLink, Loader2, Monitor, Pencil, Plus, RefreshCw, Tv2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_FORM = {
  name: '',
  print_groups: [],
  category_ids: [],
  menu_ids: [],
  view_type: 'table',
  theme: 'light',
  font_size: 'base',
  grid_cols: 4,
  warn_minutes: 8,
  alert_minutes: 15,
  show_order_source: true,
  show_fulfillment_type: true,
};

function normalizeIdList(values) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ),
  );
}

function normalizeMonitorProfile(profile = {}) {
  return {
    ...DEFAULT_FORM,
    ...profile,
    print_groups: (profile.print_groups || []).map((group) => canonicalPrintGroupLabel(group)),
    category_ids: normalizeIdList(profile.category_ids),
    menu_ids: normalizeIdList(profile.menu_ids),
    grid_cols: Number(profile.grid_cols) || 4,
    warn_minutes: Number(profile.warn_minutes) || 8,
    alert_minutes: Number(profile.alert_minutes) || 15,
    show_order_source: profile.show_order_source ?? true,
    show_fulfillment_type: profile.show_fulfillment_type ?? true,
  };
}

function extractMenuOptions(settings) {
  const salesCatalog = normalizeSalesCatalog(settings?.manager_portal_config?.salesCatalog || { menus: [] });
  return salesCatalog.menus
    .filter((menu) => menu.enabled !== false)
    .map((menu) => ({
      id: menu.id,
      name: menu.name,
      scope: menu.scope,
      scopeLabel: SALES_MENU_SCOPE_LABELS[menu.scope] || menu.scope,
    }));
}

function toggleValue(values, nextValue) {
  return values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue];
}

function FilterSection({
  label,
  hint,
  items,
  selectedValues,
  onToggle,
  onSelectAll,
  onClear,
  emptyText,
  getKey,
  renderLabel,
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label>{label}</Label>
          {hint ? <p className="text-xs text-slate-400 mt-0.5">{hint}</p> : null}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-slate-500 hover:text-slate-900"
          >
            Alle anzeigen
          </button>
          <button
            type="button"
            onClick={onClear}
            className="text-slate-500 hover:text-slate-900"
          >
            Alles markieren
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {items.map((item) => {
          const key = getKey(item);
          const isSelected = selectedValues.includes(key);

          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                isSelected
                  ? 'bg-[#1a2f38] text-white border-[#1a2f38]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {renderLabel(item)}
            </button>
          );
        })}
        {items.length === 0 ? (
          <p className="text-slate-400 text-sm">{emptyText}</p>
        ) : null}
      </div>
    </div>
  );
}

function FilterBadgeBlock({ title, values, emptyLabel, renderValue }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{title}</p>
      <div className="flex flex-wrap gap-1">
        {values.length === 0 ? (
          <span className="text-slate-400 text-sm">{emptyLabel}</span>
        ) : (
          values.map((value) => (
            <span key={value} className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">
              {renderValue(value)}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminMonitors() {
  const [profiles, setProfiles] = useState([]);
  const [printGroups, setPrintGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [profilesData, printGroupsData, categoriesData, settingsData] = await Promise.all([
        getMonitorProfiles(),
        getPrintGroups(),
        getCategories(),
        getSettings(),
      ]);

      setProfiles(profilesData.map((profile) => normalizeMonitorProfile(profile)));
      setPrintGroups(printGroupsData || []);
      setCategories(categoriesData || []);
      setMenuOptions(extractMenuOptions(settingsData));
    } catch (error) {
      console.error(error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (profile = null) => {
    setEditing(profile);
    setForm(normalizeMonitorProfile(profile || {}));
    setShowDialog(true);
  };

  const togglePrintGroup = (groupName) => {
    setForm((current) => ({
      ...current,
      print_groups: includesPrintGroup(current.print_groups, groupName)
        ? current.print_groups.filter((group) => !includesPrintGroup([group], groupName))
        : [...current.print_groups, canonicalPrintGroupLabel(groupName)],
    }));
  };

  const toggleFilterValue = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: toggleValue(current[field] || [], value),
    }));
  };

  const categoryMap = Object.fromEntries(
    categories.map((category) => [category.id, category.name]),
  );
  const menuMap = Object.fromEntries(
    menuOptions.map((menu) => [menu.id, menu.name]),
  );

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Bitte Namen eingeben');
      return;
    }

    const payload = {
      ...normalizeMonitorProfile(form),
      name: form.name.trim(),
    };

    try {
      if (editing) {
        await updateMonitorProfile(editing.id, payload);
        toast.success('Monitor aktualisiert');
      } else {
        await createMonitorProfile(payload);
        toast.success('Monitor erstellt');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error('Fehler beim Speichern');
    }
  };

  const openMonitor = (profileId) => {
    window.open(`/monitor/${profileId}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Bestellmonitore</h1>
          <p className="text-slate-500">{profiles.length} Monitor-Profile · Küche & Bar Kanban</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Monitor
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Tv2 className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-0.5">Kanban-Monitor für Küche & Bar</p>
          <p className="text-blue-600">
            Jedes Profil kann jetzt Druckergruppen, Artikelkategorien und Menüs separat einschränken.
            So entscheiden Sie pro Bildschirm genau, welche Bons sichtbar sind.
          </p>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Profil</TableHead>
                <TableHead>Filter</TableHead>
                <TableHead className="text-center">Alert nach</TableHead>
                <TableHead className="w-[140px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Monitor className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">{profile.name}</p>
                        {profile.is_active && (
                          <div className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Aktiv
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="space-y-3 py-4">
                    <FilterBadgeBlock
                      title="Bons"
                      values={profile.print_groups || []}
                      emptyLabel="Alle Druckergruppen"
                      renderValue={(value) => canonicalPrintGroupLabel(value)}
                    />
                    <FilterBadgeBlock
                      title="Kategorien"
                      values={profile.category_ids || []}
                      emptyLabel="Alle Kategorien"
                      renderValue={(value) => categoryMap[value] || value}
                    />
                    <FilterBadgeBlock
                      title="Menüs"
                      values={profile.menu_ids || []}
                      emptyLabel="Alle Menüs"
                      renderValue={(value) => menuMap[value] || value}
                    />
                  </TableCell>
                  <TableCell className="text-center text-slate-600">
                    <span className="text-amber-600">{profile.warn_minutes ?? 8}'</span>
                    {' / '}
                    <span className="text-red-600">{profile.alert_minutes ?? 15}'</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openDialog(profile)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 gap-1"
                        onClick={() => openMonitor(profile.id)}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Öffnen
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {profiles.length === 0 && (
            <div className="text-center py-12">
              <Monitor className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Noch keine Monitor-Profile</p>
              <Button className="mt-4 bg-[#1a2f38]" onClick={() => openDialog()}>
                Ersten Monitor erstellen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Monitor bearbeiten' : 'Neuer Bestellmonitor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="z.B. Küche, Bar, Ausgabe"
                autoFocus
              />
            </div>

            <FilterSection
              label="Druckergruppen anzeigen"
              hint="Leer = alle Küche-/Bar-Bons anzeigen"
              items={printGroups}
              selectedValues={form.print_groups || []}
              onToggle={(value) => togglePrintGroup(value)}
              onSelectAll={() => setForm((current) => ({ ...current, print_groups: [] }))}
              onClear={() => setForm((current) => ({ ...current, print_groups: printGroups.map((group) => canonicalPrintGroupLabel(group.name)) }))}
              emptyText="Noch keine Druckergruppen vorhanden"
              getKey={(group) => canonicalPrintGroupLabel(group.name)}
              renderLabel={(group) => group.name}
            />

            <FilterSection
              label="Kategorien"
              hint="Leer = alle Kategorien anzeigen"
              items={categories}
              selectedValues={form.category_ids || []}
              onToggle={(value) => toggleFilterValue('category_ids', value)}
              onSelectAll={() => setForm((current) => ({ ...current, category_ids: [] }))}
              onClear={() => setForm((current) => ({ ...current, category_ids: categories.map((category) => category.id) }))}
              emptyText="Noch keine Kategorien vorhanden"
              getKey={(category) => category.id}
              renderLabel={(category) => category.name}
            />

            <FilterSection
              label="Menüs"
              hint="Leer = alle Menüs anzeigen"
              items={menuOptions}
              selectedValues={form.menu_ids || []}
              onToggle={(value) => toggleFilterValue('menu_ids', value)}
              onSelectAll={() => setForm((current) => ({ ...current, menu_ids: [] }))}
              onClear={() => setForm((current) => ({ ...current, menu_ids: menuOptions.map((menu) => menu.id) }))}
              emptyText="Noch keine Menüs vorhanden"
              getKey={(menu) => menu.id}
              renderLabel={(menu) => `${menu.name} · ${menu.scopeLabel}`}
            />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ansichtstyp</Label>
                <select
                  className="w-full mt-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  value={form.view_type || 'table'}
                  onChange={(event) => setForm({ ...form, view_type: event.target.value })}
                >
                  <option value="table">Tabelle (Liste)</option>
                  <option value="kanban">Kanban (Spalten)</option>
                </select>
              </div>
              <div>
                <Label>Design (Farbe)</Label>
                <select
                  className="w-full mt-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  value={form.theme || 'light'}
                  onChange={(event) => setForm({ ...form, theme: event.target.value })}
                >
                  <option value="light">Hell (Standard)</option>
                  <option value="dark">Dunkel (Klassisch)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Schriftgröße</Label>
                <select
                  className="w-full mt-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  value={form.font_size || 'base'}
                  onChange={(event) => setForm({ ...form, font_size: event.target.value })}
                >
                  <option value="sm">Klein</option>
                  <option value="base">Mittel</option>
                  <option value="lg">Groß</option>
                  <option value="xl">Sehr Groß</option>
                </select>
              </div>
              <div>
                <Label>Spalten (Kanban)</Label>
                <Input
                  type="number"
                  min={1}
                  max={8}
                  value={form.grid_cols || 4}
                  onChange={(event) => setForm({ ...form, grid_cols: parseInt(event.target.value, 10) || 4 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Warnung (Min.)</Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={form.warn_minutes}
                  onChange={(event) => setForm({ ...form, warn_minutes: parseInt(event.target.value, 10) || 8 })}
                />
              </div>
              <div>
                <Label>Alarm (Min.)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={form.alert_minutes}
                  onChange={(event) => setForm({ ...form, alert_minutes: parseInt(event.target.value, 10) || 15 })}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.show_order_source ?? true}
                  onChange={(event) => setForm({ ...form, show_order_source: event.target.checked })}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                />
                <span className="text-sm text-slate-600">Bestellquelle anzeigen (Wolt, Lieferando etc.)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.show_fulfillment_type ?? true}
                  onChange={(event) => setForm({ ...form, show_fulfillment_type: event.target.checked })}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                />
                <span className="text-sm text-slate-600">Bereitstellungstyp anzeigen (Tisch/Abholung)</span>
              </label>
            </div>

            <Button onClick={handleSave} className="w-full bg-[#1a2f38] mt-2">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
